import React, { useState } from 'react';
import { Button, Popover, FormControlLabel, Radio, RadioGroup, Typography, Grid, Box } from '@mui/material';
import useResponsive from '../Hooks/useResponsive';

const MoreFilters = ({ filters, handleFilterChange, locationOptions = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCheckboxChange = (filterType, value) => {
    if (filterType === 'propertyType') {
      handleFilterChange(filterType, value);
    } else {
      handleFilterChange(filterType, [value]);
    }
    setTimeout(handleClose, 300);
  };

  const { isExtraSmall, isSmallScreen } = useResponsive();

  return (
    <>
      <Button
        disableFocusRipple disableRipple
        onClick={handleClick} variant="outlined"
        fullWidth
        sx={{
          height: { xs: 44, sm: 40 },
          borderColor: '#1C1917',
          backgroundColor: 'white',
          color: '#1C1917',
          whiteSpace: 'nowrap',
          flex: 1,
          minWidth: 0,
          fontSize: isExtraSmall ? '0.8125rem' : isSmallScreen ? '0.875rem' : '0.875rem',
          px: isExtraSmall ? 1.5 : 2,
          py: isExtraSmall ? '6px' : '8px',
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '0.01em',
          '&:hover': {
            backgroundColor: '#F3F4F6',
            borderColor: '#1C1917',
          },
        }}
        startIcon={
          <i
            className="fas fa-sliders"
            style={{
              fontSize: isExtraSmall ? '0.8rem' : isSmallScreen ? '0.875rem' : '0.875rem',
              marginRight: '6px',
              transition: 'none',
            }}
          />
        }
      >
        {isExtraSmall ? 'Filters' : 'More Filters'}
      </Button>

      <Popover open={open} anchorEl={anchorEl} onClose={handleClose}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: isExtraSmall ? 'center' : 'left', }}
        transformOrigin={{
          vertical: 'top', horizontal: isExtraSmall ? 'center' : 'left',
        }}
        PaperProps={{
          sx: {
            width: isExtraSmall ? 'calc(100% - 32px)' : isSmallScreen ? '90%' : 700,
            maxWidth: '100%',
            maxHeight: isExtraSmall ? '75vh' : '80vh',
            overflowY: 'auto',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
          },
        }}
        sx={{
          '& .MuiPopover-paper': {
            marginTop: isExtraSmall ? '8px' : '4px',
          },
        }}
      >
        <Box sx={{ p: { xs: isExtraSmall ? 1.5 : 2, sm: 2.5, md: 3 }, width: '100%', }}>
          <Grid container spacing={{ xs: isExtraSmall ? 1.5 : 2, sm: 2.5 }}>
            {/* Project Status */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" gutterBottom sx={{
                fontSize: isExtraSmall ? '0.8125rem' : '0.875rem',
                fontWeight: 600, color: 'text.secondary',
                pl: isExtraSmall ? 0.5 : 1, mb: isExtraSmall ? 0.5 : 1,
              }}>
                Project Status
              </Typography>
              <RadioGroup
                value={(Array.isArray(filters.projectStatus) && filters.projectStatus[0]) || ''}
                onChange={(e) => handleCheckboxChange('projectStatus', e.target.value)}
              >
                {['Launching Soon', 'New Launch', 'Under Construction', 'Ready to Move In'].map((label) => (
                  <FormControlLabel
                    key={label}
                    value={label}
                    control={
                      <Radio
                        size={isExtraSmall ? 'small' : 'medium'}
                        sx={{
                          padding: isExtraSmall ? '4px' : '8px',
                          '& .MuiSvgIcon-root': {
                            fontSize: isExtraSmall ? '1.25rem' : '1.5rem',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: isExtraSmall ? '0.8125rem' : '0.875rem', lineHeight: 1.3, }} > {label}</Typography>
                    }
                    sx={{
                      margin: 0,
                      '& .MuiFormControlLabel-label': {
                        padding: isExtraSmall ? '4px 0' : '6px 0',
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </Grid>

            {/* Property Type */}
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="subtitle2" gutterBottom sx={{
                fontSize: isExtraSmall ? '0.8125rem' : '0.875rem',
                fontWeight: 600, color: 'text.secondary',
                pl: isExtraSmall ? 0.5 : 1, mb: isExtraSmall ? 0.5 : 1,
              }}>
                Property Type
              </Typography>
              <RadioGroup
                value={filters.propertyType || ''}
                onChange={(e) => handleCheckboxChange('propertyType', e.target.value)}
              >
                {[{label: 'Apartment', value: 'Apartment'}, {label: 'Villa', value: 'Villa'}, {label: 'Plots', value: 'Plot'}].map((type) => (
                  <FormControlLabel
                    key={type.value}
                    value={type.value}
                    control={
                      <Radio
                        size={isExtraSmall ? 'small' : 'medium'}
                        sx={{
                          padding: isExtraSmall ? '4px' : '8px',
                          '& .MuiSvgIcon-root': {
                            fontSize: isExtraSmall ? '1.25rem' : '1.5rem',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: isExtraSmall ? '0.8125rem' : '0.875rem',
                          lineHeight: 1.3,
                        }}
                      >
                        {type.label}
                      </Typography>
                    }
                    sx={{
                      margin: 0,
                      '& .MuiFormControlLabel-label': {
                        padding: isExtraSmall ? '4px 0' : '6px 0',
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </Grid>

            {/* Availablity */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" gutterBottom sx={{
                fontSize: isExtraSmall ? '0.8125rem' : '0.875rem',
                fontWeight: 600,
                color: 'text.secondary',
                pl: isExtraSmall ? 0.5 : 1,
                mb: isExtraSmall ? 0.5 : 1,
              }}>
                Availablity
              </Typography>
              <RadioGroup
                value={(Array.isArray(filters.availability) && filters.availability[0]) || ''}
                onChange={(e) => handleCheckboxChange('availability', e.target.value)}
              >
                {['Resale', 'New', 'Rent', 'Lease'].map((label) => (
                  <FormControlLabel
                    key={label}
                    value={label}
                    control={
                      <Radio
                        size={isExtraSmall ? 'small' : 'medium'}
                        sx={{
                          padding: isExtraSmall ? '4px' : '8px',
                          '& .MuiSvgIcon-root': {
                            fontSize: isExtraSmall ? '1.25rem' : '1.5rem',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: isExtraSmall ? '0.8125rem' : '0.875rem',
                          lineHeight: 1.3,
                        }}
                      >
                        {label}
                      </Typography>
                    }
                    sx={{
                      margin: 0,
                      '& .MuiFormControlLabel-label': {
                        padding: isExtraSmall ? '4px 0' : '6px 0',
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </Grid>

            {/* Area */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" gutterBottom sx={{
                fontSize: isExtraSmall ? '0.8125rem' : '0.875rem',
                fontWeight: 600, color: 'text.secondary',
                pl: isExtraSmall ? 0.5 : 1, mb: isExtraSmall ? 0.5 : 1,
              }}>
                Area (sqft)
              </Typography>
              <RadioGroup
                value={(Array.isArray(filters.area) && filters.area[0]) || ''}
                onChange={(e) => handleCheckboxChange('area', e.target.value)}
              >
                {['850-1250', '1250-1750', '1750-2250', '2250-2750', '2750-3250', 'more'].map((label) => (
                  <FormControlLabel
                    key={label}
                    value={label}
                    control={
                      <Radio
                        size={isExtraSmall ? 'small' : 'medium'}
                        sx={{
                          padding: isExtraSmall ? '4px' : '8px',
                          '& .MuiSvgIcon-root': {
                            fontSize: isExtraSmall ? '1.25rem' : '1.5rem',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: isExtraSmall ? '0.8125rem' : '0.875rem',
                          lineHeight: 1.3,
                        }}
                      >
                        {label} sq ft
                      </Typography>
                    }
                    sx={{
                      margin: 0,
                      '& .MuiFormControlLabel-label': {
                        padding: isExtraSmall ? '4px 0' : '6px 0',
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </Grid>

            {/* Location (from API) */}
            {locationOptions.length > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" gutterBottom sx={{
                  fontSize: isExtraSmall ? '0.8125rem' : '0.875rem',
                  fontWeight: 600, color: 'text.secondary',
                  pl: isExtraSmall ? 0.5 : 1, mb: isExtraSmall ? 0.5 : 1,
                }}>
                  More Locations
                </Typography>
                <RadioGroup
                  value={(Array.isArray(filters.moreLocations) && filters.moreLocations[0]) || ''}
                  onChange={(e) => handleCheckboxChange('moreLocations', e.target.value)}
                >
                  {locationOptions.map((loc) => (
                    <FormControlLabel
                      key={loc}
                      value={loc}
                      control={
                        <Radio
                          size={isExtraSmall ? 'small' : 'medium'}
                          sx={{
                            padding: isExtraSmall ? '4px' : '8px',
                            '& .MuiSvgIcon-root': {
                              fontSize: isExtraSmall ? '1.25rem' : '1.5rem',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: isExtraSmall ? '0.8125rem' : '0.875rem',
                            lineHeight: 1.3,
                          }}
                        >
                          {loc}
                        </Typography>
                      }
                      sx={{
                        margin: 0,
                        '& .MuiFormControlLabel-label': {
                          padding: isExtraSmall ? '4px 0' : '6px 0',
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              </Grid>
            )}
          </Grid>
        </Box>
      </Popover>
    </>
  );
};

export default MoreFilters;
